import { useCallback } from 'react';

import { useRuleStore } from '../state/ruleStore';

import type { ConditionGroup, ConditionLeaf, LogicalOp } from '../types/rule';

function cloneRoot(root: ConditionGroup): ConditionGroup {
  return structuredClone(root);
}

export function useConditionBuilder() {
  const { draft, setConditionsRoot } = useRuleStore();

  const ensureRoot = useCallback(() => {
    if (!draft.conditionsRoot) {
      setConditionsRoot({
        id: (crypto as any)?.randomUUID?.() ?? Math.random().toString(36).slice(2),
        type: 'group',
        logical: 'AND',
        children: [],
      });
    }
  }, [draft.conditionsRoot, setConditionsRoot]);

  const addRule = useCallback(
    (init?: Partial<ConditionLeaf>) => {
      if (!draft.conditionsRoot) return;
      const root = cloneRoot(draft.conditionsRoot);
      const leaf: ConditionLeaf = {
        id: (crypto as any)?.randomUUID?.() ?? Math.random().toString(36).slice(2),
        type: 'rule',
        left: init?.left ?? 'submission.option_id',
        operator: init?.operator ?? 'EQUALS',
        right: init?.right ?? '',
        meta: init?.meta,
      };
      root.children.push(leaf);
      setConditionsRoot(root);
    },
    [draft.conditionsRoot, setConditionsRoot]
  );

  const addGroup = useCallback(
    (logical: LogicalOp = 'AND') => {
      if (!draft.conditionsRoot) return;
      const root = cloneRoot(draft.conditionsRoot);
      root.children.push({
        id: (crypto as any)?.randomUUID?.() ?? Math.random().toString(36).slice(2),
        type: 'group',
        logical,
        children: [],
      });
      setConditionsRoot(root);
    },
    [draft.conditionsRoot, setConditionsRoot]
  );

  const deleteChildAt = useCallback(
    (index: number) => {
      if (!draft.conditionsRoot) return;
      const root = cloneRoot(draft.conditionsRoot);
      root.children.splice(index, 1);
      setConditionsRoot(root);
    },
    [draft.conditionsRoot, setConditionsRoot]
  );

  const setGroupLogical = useCallback(
    (logical: LogicalOp) => {
      if (!draft.conditionsRoot) return;
      const root = cloneRoot(draft.conditionsRoot);
      root.logical = logical;
      setConditionsRoot(root);
    },
    [draft.conditionsRoot, setConditionsRoot]
  );

  const updateLeafAt = useCallback(
    (index: number, patch: Partial<ConditionLeaf>) => {
      if (!draft.conditionsRoot) return;
      const root = cloneRoot(draft.conditionsRoot);
      root.children[index] = { ...(root.children[index] as ConditionLeaf), ...patch };
      setConditionsRoot(root);
    },
    [draft.conditionsRoot, setConditionsRoot]
  );

  const updateGroupAt = useCallback(
    (index: number, patch: Partial<ConditionGroup>) => {
      if (!draft.conditionsRoot) return;
      const root = cloneRoot(draft.conditionsRoot);
      root.children[index] = { ...(root.children[index] as ConditionGroup), ...patch };
      setConditionsRoot(root);
    },
    [draft.conditionsRoot, setConditionsRoot]
  );

  const deleteChildAtPath = useCallback(
    (path: number[]) => {
      if (!draft.conditionsRoot || path.length === 0) return;
      const root = cloneRoot(draft.conditionsRoot);

      if (path.length === 1) {
        root.children.splice(path[0], 1);
        setConditionsRoot(root);
        return;
      }

      // Para paths mais profundos, implementar recursão
      let current = root;
      for (let i = 0; i < path.length - 1; i++) {
        current = current.children[path[i]] as ConditionGroup;
      }
      current.children.splice(path[path.length - 1], 1);
      setConditionsRoot(root);
    },
    [draft.conditionsRoot, setConditionsRoot]
  );

  const addRuleToGroup = useCallback(
    (groupPath: number[], init?: Partial<ConditionLeaf>) => {
      if (!draft.conditionsRoot) return;
      const root = cloneRoot(draft.conditionsRoot);

      const leaf: ConditionLeaf = {
        id: (crypto as any)?.randomUUID?.() ?? Math.random().toString(36).slice(2),
        type: 'rule',
        left: init?.left ?? 'submission.option_id',
        operator: init?.operator ?? 'EQUALS',
        right: init?.right ?? '',
        meta: init?.meta,
      };

      if (groupPath.length === 0) {
        root.children.push(leaf);
        setConditionsRoot(root);
        return;
      }

      let current = root;
      for (const index of groupPath) {
        current = current.children[index] as ConditionGroup;
      }
      current.children.push(leaf);
      setConditionsRoot(root);
    },
    [draft.conditionsRoot, setConditionsRoot]
  );

  const addGroupToGroup = useCallback(
    (groupPath: number[], logical: LogicalOp = 'AND') => {
      if (!draft.conditionsRoot) return;
      const root = cloneRoot(draft.conditionsRoot);

      const newGroup: ConditionGroup = {
        id: (crypto as any)?.randomUUID?.() ?? Math.random().toString(36).slice(2),
        type: 'group',
        logical,
        children: [],
      };

      if (groupPath.length === 0) {
        root.children.push(newGroup);
        setConditionsRoot(root);
        return;
      }

      let current = root;
      for (const index of groupPath) {
        current = current.children[index] as ConditionGroup;
      }
      current.children.push(newGroup);
      setConditionsRoot(root);
    },
    [draft.conditionsRoot, setConditionsRoot]
  );

  return {
    ensureRoot,
    addRule,
    addGroup,
    deleteChildAt,
    setGroupLogical,
    updateLeafAt,
    updateGroupAt,
    deleteChildAtPath,
    addRuleToGroup,
    addGroupToGroup,
  };
}
